import React, { useEffect, useState } from "react";
import api, { fmtMoney, fmtCompactMoney } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import MetricCard from "@/components/MetricCard";
import OnboardingChecklist from "@/components/OnboardingChecklist";
import DateRangePicker from "@/components/DateRangePicker";
import NewDocumentsAlert from "@/components/NewDocumentsAlert";
import {
  ArrowDownRight, ArrowUpRight, Wallet, Flame, Timer, AlertTriangle, AlertOctagon, ClipboardList, ArrowRight,
} from "lucide-react";
import {
  Bar, BarChart, CartesianGrid, Cell, Legend, Line, ResponsiveContainer,
  Tooltip, XAxis, YAxis, ComposedChart, Pie, PieChart,
} from "recharts";

const COLORS = ["#0F4C3A", "#D97736", "#5C8A78", "#1A1A1A", "#A8C9BB", "#525252"];

function MonthlyChart({ data }) {
  return (
    <div className="p-6 rounded-lg border border-black bg-sky-50" data-testid="monthly-flow-chart">
      <div className="flex items-end justify-between mb-5">
        <div>
          <div className="overline mb-1.5">Monthly Cash Flow</div>
          <h3 className="font-heading text-2xl font-medium tracking-tight">Income vs. Expenses</h3>
        </div>
      </div>
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E3" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#8A8A8A" }} tickLine={false} axisLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "#8A8A8A" }} tickLine={false} axisLine={false} tickFormatter={fmtCompactMoney} />
            <Tooltip
              formatter={(v) => fmtMoney(v)}
              contentStyle={{ background: "#fff", border: "1px solid #E5E5E3", borderRadius: 8, fontSize: 12 }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="income" fill="#0F4C3A" radius={[6, 6, 0, 0]} name="Income" />
            <Bar dataKey="expenses" fill="#D97736" radius={[6, 6, 0, 0]} name="Expenses" />
            <Line type="monotone" dataKey="net" stroke="#1A1A1A" strokeWidth={2} dot={{ r: 3 }} name="Net" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function CategoryBreakdown({ data }) {
  return (
    <div className="card-surface p-6" data-testid="category-breakdown">
      <div className="overline mb-1.5">Top Expense Categories</div>
      <h3 className="font-heading text-2xl font-medium tracking-tight mb-5">Where money goes</h3>
      {data.length === 0 ? (
        <div className="text-sm text-ink-lighter">No expense data yet.</div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-6 items-center">
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data} dataKey="amount" nameKey="category" innerRadius={45} outerRadius={75} paddingAngle={2}>
                  {data.map((entry, i) => <Cell key={entry.category} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => fmtMoney(v)} contentStyle={{ background: "#fff", border: "1px solid #E5E5E3", borderRadius: 8, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2">
            {data.slice(0, 6).map((c, i) => (
              <div key={c.category} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ background: COLORS[i % COLORS.length] }} />
                  <span className="text-ink">{c.category}</span>
                </div>
                <span className="num text-ink-light">{fmtMoney(c.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [planSummary, setPlanSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState({ start: "", end: "" });
  const [compare, setCompare] = useState(false);

  useEffect(() => {
    const params = range.start && range.end
      ? { start: range.start, end: range.end, ...(compare ? { compare: 1 } : {}) }
      : {};
    Promise.all([
      api.get("/dashboard/overview", { params }).then((r) => setData(r.data)),
      planSummary === null
        ? api.get("/plans/summary/dashboard").then((r) => setPlanSummary(r.data)).catch(() => {})
        : Promise.resolve(),
    ]).finally(() => setLoading(false));
  }, [range.start, range.end, compare]);

  if (loading && !data) return <div className="p-8 text-ink-lighter">Loading…</div>;
  if (!data) return <div className="p-8 text-destructive">Could not load dashboard.</div>;

  const netPositive = data.ytd_net >= 0;
  const rangeActive = !!data.range_active;
  const mtdSubLabel = rangeActive ? "In range" : "Month to date";
  const ytdSubLabel = rangeActive ? "In range" : "Year to date";
  const cmp = data.compare;
  const deltaLabel = "vs prior";
  const dIncome = cmp ? { pct: cmp.delta_income_pct, label: deltaLabel, invert: false } : null;
  const dExpenses = cmp ? { pct: cmp.delta_expenses_pct, label: deltaLabel, invert: true } : null;
  const dFees = cmp ? { pct: cmp.delta_fees_pct, label: deltaLabel, invert: true } : null;
  const dNet = cmp ? { pct: cmp.delta_net_pct, label: deltaLabel, invert: false } : null;

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="dashboard-page">
      <div className="mb-10 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="overline mb-2">Overview</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight">
            Good to see you, {user?.name?.split(" ")[0] || "there"}.
          </h1>
          <p className="text-ink-light mt-2">Here&apos;s your business — at a glance.</p>
        </div>
        <div className="text-right">
          <div className="overline mb-1">Cash position</div>
          <div className={`num font-heading text-3xl font-medium ${netPositive ? "text-forest" : "text-destructive"}`}>
            {fmtMoney(data.cash_position)}
          </div>
        </div>
      </div>

      <OnboardingChecklist />

      <NewDocumentsAlert />

      <DateRangePicker
        value={range}
        onChange={setRange}
        compare={compare}
        onCompareChange={setCompare}
      />

      <div className="grid grid-cols-2 md:grid-cols-3 2xl:grid-cols-5 gap-4 mb-6">
        <MetricCard
          label={rangeActive ? "Range Cash In" : "MTD Cash In"} testid="metric-mtd-income" icon={ArrowUpRight}
          value={fmtMoney(data.mtd_income)}
          accent="text-forest"
          tone="forest"
          sub={`${ytdSubLabel} · ${fmtMoney(data.ytd_income)}`}
          delta={dIncome}
          tip={
            <>
              <strong className="block text-paper mb-1">Month-to-date cash in</strong>
              <span className="block opacity-90">Every dollar your business has actually received this calendar month. Excludes invoices that haven&apos;t been paid.</span>
            </>
          }
          tipAction={{
            label: "Ask AI: how do I accelerate receivables?",
            to: "/app/advisor?src=mtd-cash-in-tip&q=" + encodeURIComponent(
              "Based on my real numbers, how can I speed up customer payments? Give me 3 concrete tactics — invoicing cadence, payment terms, deposits — with the dollar impact each could have on my monthly cash in."
            ),
          }}
        />
        <MetricCard
          label={rangeActive ? "Range Cash Out" : "MTD Cash Out"} testid="metric-mtd-expenses" icon={ArrowDownRight}
          value={fmtMoney(data.mtd_expenses)}
          accent="text-ochre"
          tone="ochre"
          sub={`${ytdSubLabel} · ${fmtMoney(data.ytd_expenses)}`}
          delta={dExpenses}
          tip={
            <>
              <strong className="block text-paper mb-1">Month-to-date cash out</strong>
              <span className="block opacity-90">Everything you&apos;ve spent so far this month. Includes recurring software, payroll, rent, contractors, and one-offs.</span>
            </>
          }
          tipAction={{
            label: "Ask AI: where can I cut 20%?",
            to: "/app/advisor?src=mtd-cash-out-tip&q=" + encodeURIComponent(
              "Looking at my spending, where can I cut 20% in the next 30 days without hurting the business? Show me top categories by potential savings, exact dollar amounts, and how to make each cut."
            ),
          }}
        />
        <MetricCard
          label="Runway" testid="metric-runway" icon={Timer}
          value={data.runway_months != null ? `${data.runway_months} mo` : "—"}
          tone="navy"
          sub={`Avg burn · ${fmtMoney(data.avg_monthly_burn)}`}
          tip={
            <>
              <strong className="block text-paper mb-1">Runway = months of cash left</strong>
              <span className="block opacity-90">
                How long your business can keep going at the current burn rate.
              </span>
              <span className="block mt-1.5 opacity-90">
                Formula: <em>cash position ÷ avg monthly burn</em>.
                Burn is averaged over the last 3 completed months.
              </span>
            </>
          }
          tipAction={{
            label: "Ask AI: how do I extend my runway?",
            to: "/app/advisor?src=runway-tip&q=" + encodeURIComponent(
              "Based on my real numbers, what are the 3 highest-impact moves I can make in the next 90 days to extend my runway by at least 3 months? Be specific and quantitative."
            ),
          }}
        />
        <MetricCard
          label="Total Debt" testid="metric-total-debt" icon={Wallet}
          value={fmtMoney(data.total_debt)}
          tone="plum"
          sub={`Monthly · ${fmtMoney(data.monthly_debt_obligations)}`}
          tip={
            <>
              <strong className="block text-paper mb-1">Outstanding business debt</strong>
              <span className="block opacity-90">Sum of all loan and credit-card balances BizCashflow Pros is tracking. Monthly minimums shown below.</span>
            </>
          }
          tipAction={{
            label: "Ask AI: pick the right payoff plan",
            to: "/app/advisor?src=total-debt-tip&q=" + encodeURIComponent(
              "Given my debts, should I use snowball or avalanche? Calculate which strategy saves me more interest, how many months earlier I'd be debt-free, and which specific debt I should attack first this month."
            ),
          }}
        />
        <MetricCard
          label={rangeActive ? "Fees & NSF (Range)" : "Fees & NSF (YTD)"} testid="metric-fees-ytd" icon={AlertOctagon}
          value={fmtMoney(data.fees_ytd?.fees_grand_total || 0)}
          accent={(data.fees_ytd?.fees_grand_count || 0) > 0 ? "text-ochre" : "text-ink"}
          tone="rose"
          delta={dFees}
          sub={
            (data.fees_recovered_ytd?.total_ytd || 0) > 0
              ? `${fmtMoney(data.fees_recovered_ytd.total_ytd)} recovered YTD`
              : (data.fees_ytd?.fees_grand_count || 0) > 0
                ? `${data.fees_ytd.nsf_count} NSF · ${data.fees_ytd.bank_fees_count} other`
                : "No bank fees detected"
          }
          tip={
            <>
              <strong className="block text-paper mb-1">Bank fees & NSF charges (YTD)</strong>
              <span className="block opacity-90">Service charges, wire fees, overdrafts and NSF events your bank has billed you this year — many are refundable on request.</span>
            </>
          }
          tipAction={{
            label: "Open the refund-email tool",
            to: "/app/statements?src=fees-tip#refund",
          }}
        />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2">
          <MonthlyChart data={data.monthly_series} />
        </div>
        <div className="space-y-6">
          <div className="card-surface p-6" data-testid="net-summary">
            <div className="overline mb-1.5">{rangeActive ? "Net (range)" : "Net YTD"}</div>
            <div className={`num font-heading text-4xl font-medium tracking-tight ${netPositive ? "text-forest" : "text-destructive"}`}>
              {fmtMoney(data.ytd_net)}
            </div>
            {dNet && dNet.pct != null && (
              <div
                className={`inline-flex items-center gap-1 mt-2 text-xs font-medium px-1.5 py-0.5 rounded ${
                  dNet.pct === 0
                    ? "bg-ink-lighter/10 text-ink-light"
                    : dNet.pct > 0
                      ? "bg-forest/10 text-forest"
                      : "bg-destructive/10 text-destructive"
                }`}
                data-testid="net-summary-delta"
              >
                {dNet.pct > 0 ? "+" : ""}{dNet.pct}% <span className="text-ink-lighter font-normal">vs prior</span>
              </div>
            )}
            <div className="text-sm text-ink-light mt-2">
              {netPositive
                ? "Cash flow positive. Keep stacking."
                : "Negative. Review burn or boost revenue."}
            </div>
          </div>
          <div className="card-surface p-6" data-testid="advice-card">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-ochre/15 text-ochre flex items-center justify-center shrink-0">
                <Flame className="w-4 h-4" strokeWidth={1.75} />
              </div>
              <div>
                <div className="font-heading text-lg font-medium mb-1">Action of the week</div>
                <p className="text-sm text-ink-light leading-relaxed">
                  Your avalanche-method debt plan saves more than minimum-only payments. Ask the AI advisor for a quarterly plan.
                </p>
              </div>
            </div>
          </div>
          {planSummary && planSummary.latest && (
            <Link to={`/app/plan/${planSummary.latest.id}`} className="card-surface card-hover p-6 block" data-testid="plan-tracker-card">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg bg-forest text-white flex items-center justify-center shrink-0">
                  <ClipboardList className="w-4 h-4" strokeWidth={1.75} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="overline mb-0.5">Your 90-day plan</div>
                  <div className="font-heading text-base font-medium truncate" data-testid="plan-tracker-title">{planSummary.latest.title}</div>
                </div>
                <ArrowRight className="w-4 h-4 text-ink-lighter shrink-0 mt-2" />
              </div>
              <div className="flex items-baseline justify-between mb-2 text-xs text-ink-light">
                <span><span className="num font-medium text-ink">{planSummary.latest.actions_done}</span> of <span className="num">{planSummary.latest.actions_total}</span> actions done</span>
                <span className="num">{planSummary.latest.actions_total ? Math.round((planSummary.latest.actions_done / planSummary.latest.actions_total) * 100) : 0}%</span>
              </div>
              <div className="h-1.5 rounded-full bg-paper overflow-hidden">
                <div className="h-full bg-forest transition-all"
                  style={{ width: `${planSummary.latest.actions_total ? (planSummary.latest.actions_done / planSummary.latest.actions_total) * 100 : 0}%` }} />
              </div>
            </Link>
          )}
        </div>
      </div>

      <CategoryBreakdown data={data.top_expense_categories || []} />

      {data.runway_months != null && data.runway_months < 6 && (
        <div className="mt-6 card-surface p-5 border-l-4 border-l-ochre" data-testid="runway-warning">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-ochre mt-0.5 shrink-0" strokeWidth={1.75} />
            <div>
              <div className="font-medium text-ink">Runway is tight</div>
              <div className="text-sm text-ink-light mt-1">
                You have under 6 months of runway. Trim non-essentials or aggressively grow revenue. Ask the AI advisor for a 90-day plan.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
